import './assets/main.css'

import { createPinia } from 'pinia'
import { createApp } from 'vue'

/* internationalization*/
import i18n from './i18n'

/* Sentry */
import * as Sentry from '@sentry/vue'

/* primevue*/
import AutoComplete from 'primevue/autocomplete'
import Avatar from 'primevue/avatar'
import Badge from 'primevue/badge'
import Breadcrumb from 'primevue/breadcrumb'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Checkbox from 'primevue/checkbox'
import Chip from 'primevue/chip'
import Column from 'primevue/column'
import ConfirmDialog from 'primevue/confirmdialog'
import PrimeVue from 'primevue/config'
import DataTable from 'primevue/datatable'
import DatePicker from 'primevue/datepicker'
import Dialog from 'primevue/dialog'
import Divider from 'primevue/divider'
import Fieldset from 'primevue/fieldset'
import FileUpload from 'primevue/fileupload'
import FloatLabel from 'primevue/floatlabel'
import Image from 'primevue/image'
import InputNumber from 'primevue/inputnumber'
import InputText from 'primevue/inputtext'
import Listbox from 'primevue/listbox'
import Menu from 'primevue/menu'
import Menubar from 'primevue/menubar'
import Message from 'primevue/message'
import MultiSelect from 'primevue/multiselect'
import PanelMenu from 'primevue/panelmenu'
import Password from 'primevue/password'
import Popover from 'primevue/popover'
import ProgressSpinner from 'primevue/progressspinner'
import RadioButton from 'primevue/radiobutton'
import Select from 'primevue/select'
import Tab from 'primevue/tab'
import TabList from 'primevue/tablist'
import TabPanel from 'primevue/tabpanel'
import TabPanels from 'primevue/tabpanels'
import Tabs from 'primevue/tabs'
import Tag from 'primevue/tag'
import Textarea from 'primevue/textarea'
import Toast from 'primevue/toast'
import ToggleSwitch from 'primevue/toggleswitch'
import Toolbar from 'primevue/toolbar'

import { FilterService } from '@primevue/core/api'
import Aura from '@primevue/themes/aura'
import { definePreset } from '@primevue/themes'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'

import moment from 'moment'
import 'primeflex/primeflex.css'
import 'primeicons/primeicons.css'
import App from './App.vue'
import type { Bill } from './model/Bill'
import type Customer from './model/Customer'
import { type Payload } from './model/DeliveryNote'
import type Employee from './model/Employee'
import type Vehicle from './model/Vehicle'
import router from './router'

const app = createApp(App)

app.use(createPinia())
app.use(router)

// i18n
app.use(i18n)

// Custom filters
FilterService.register('employeeFilter', (value: Employee, filter): boolean => {
  if (filter === undefined || filter === null || filter.trim() === '') {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.name.toString().toLowerCase().includes(filter.toString().toLowerCase()) ||
    value.surname.toString().toLowerCase().includes(filter.toString().toLowerCase())
  )
})

FilterService.register('vehiclesFilter', (value: Vehicle[], filter): boolean => {
  if (filter === undefined || filter === null || filter.trim() === '') {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.filter((v) => {
      return v.name.toString().toLowerCase().includes(filter.toString().toLowerCase())
    }).length > 0
  )
})

FilterService.register('vehiclesFilterMultiple', (value: Vehicle[], ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.filter((v) => {
      return ids.includes(v.id)
    }).length > 0
  )
})

FilterService.register('employeeFilterMultiple', (value: Employee, ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return ids.includes(value.id)
})

FilterService.register('vehicleFilterMultiple', (value: Vehicle, ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return ids.includes(value.id)
})

FilterService.register('customerFilterMultiple', (value: Customer, ids: string[]): boolean => {
  if (ids === undefined || ids === null || ids.length == 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return ids.includes(value.id)
})

FilterService.register('dateFilter', (value, filter): boolean => {
  if (filter === undefined || filter === null || filter.length === 0) {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  if (filter.length === 2 && filter[1] !== undefined && filter[1] !== null) {
    return (
      moment(value).isSameOrAfter(moment(filter[0]).startOf('day')) &&
      moment(value).isSameOrBefore(moment(filter[1]).endOf('day'))
    )
  }

  return true
})

FilterService.register('payloadsFilter', (value: Payload[], filter): boolean => {
  if (filter === undefined || filter === null || filter.trim() === '') {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  return (
    value.filter((v) => {
      return v.payload.toString().toLowerCase().includes(filter.toString().toLowerCase())
    }).length > 0
  )
})

FilterService.register(
  'billedFilter',
  (value: Bill | undefined, filter: boolean | undefined): boolean => {
    if (filter === undefined || filter === null) {
      return true
    }

    if (filter === true) {
      return value?.id !== undefined
    } else {
      return value?.id === undefined
    }
  }
)

const MiPreset = definePreset(Aura, {
  common: {
    font: {
      family: {
        base: `'Inter var', sans-serif`
      }
    }
  }
})

const primeVueLocale = i18n.global.locale.value === 'es' ? 'es' : 'en'
const primeVueMessages = await import(`./locales/${primeVueLocale}.json`)

app.use(PrimeVue, {
  ripple: true,
  locale: primeVueMessages.primevue,
  theme: {
    preset: MiPreset,
    options: {
      darkModeSelector: '.dark-mode-disabled'
    }
  }
})

// Primevue components
app.component('AutoComplete', AutoComplete)
app.component('Avatar', Avatar)
app.component('Badge', Badge)
app.component('Breadcrumb', Breadcrumb)
app.component('Button', Button)
app.component('DatePicker', DatePicker)
app.component('Card', Card)
app.component('Checkbox', Checkbox)
app.component('Chip', Chip)
app.component('Column', Column)
app.component('ConfirmDialog', ConfirmDialog)
app.component('DataTable', DataTable)
app.component('Dialog', Dialog)
app.component('Divider', Divider)
app.component('Select', Select)
app.component('Dropdown', Select)
app.component('Fieldset', Fieldset)
app.component('FileUpload', FileUpload)
app.component('FloatLabel', FloatLabel)
app.component('InputNumber', InputNumber)
app.component('ToggleSwitch', ToggleSwitch)
app.component('InputSwitch', ToggleSwitch)
app.component('InputText', InputText)
app.component('Listbox', Listbox)
app.component('Menu', Menu)
app.component('Menubar', Menubar)
app.component('Message', Message)
app.component('MultiSelect', MultiSelect)
app.component('Popover', Popover)
app.component('OverlayPanel', Popover)
app.component('PanelMenu', PanelMenu)
app.component('Password', Password)
app.component('ProgressSpinner', ProgressSpinner)
app.component('RadioButton', RadioButton)
app.component('Tabs', Tabs)
app.component('TabList', TabList)
app.component('Tab', Tab)
app.component('TabPanels', TabPanels)
app.component('TabPanel', TabPanel)
app.component('Tag', Tag)
app.component('Textarea', Textarea)
app.component('Toast', Toast)
app.component('Toolbar', Toolbar)
app.component('Image', Image)

app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)

const isDevMode = import.meta.env.DEV

Sentry.init({
  app,
  dsn: 'https://01ec735ffbc9e9c605e88b4c564cccdf@o4507351225204736.ingest.de.sentry.io/4507351264657488',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration({
    maskAllText: false,
    blockAllMedia: false,
    networkDetailAllowUrls: [
      window.location.origin,
      'api.kargotr.com',
      /^https:\/\/api\.kargotr\.com/
    ]
  })],
  tracesSampleRate: 1.0,
  tracePropagationTargets: ['localhost', /^https:\/\/api\.kargotr\.com/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled: !isDevMode
})

app.mount('#app')
